<template>
    <Head title="Login | DMARCeye">
        <meta
            name="description"
            content="Log in to DMARCeye to access your DMARC monitoring dashboard, review reports, and manage your domain’s email authentication in one secure place."
        />
    </Head>

    <AuthLayout>
        <div>
            <div
                class="flex w-full flex-col items-stretch rounded-[8px] bg-white px-[22px] pt-[42px] pb-[42px] sm:max-w-[374px] rounded-[12.8px] border border-[#E5E7EB]"
            >
                <div
                    class="mb-5 flex h-[21px] flex-col justify-center self-stretch text-center"
                >
                    <h1
                        class="font-inter text-[21px] font-bold leading-[100%] tracking-tight text-[#161E2E]"
                    >
                        {{ $t("vue.sign_in_to_your_account") }}
                    </h1>
                </div>

                <div
                    v-if="status"
                    class="mb-4 text-center text-[10.5px] font-medium text-green-600"
                >
                    {{ status }}
                </div>

                <form @submit.prevent="submit">
                    <div class="mb-4">
                        <p
                            class="mb-[6px] text-xs font-medium text-gray-700 text-center"
                        >
                            {{ $t("vue.sign_in_with") }}
                        </p>

                        <div class="grid grid-cols-2 gap-4">
                            <SocialiteButton
                                login
                                icon-only
                                provider="google"
                                label="Google"
                                url="google.auth.index"
                            />

                            <SocialiteButton
                                login
                                icon-only
                                provider="microsoft"
                                label="Microsoft"
                                url="microsoft.auth.index"
                            />
                        </div>
                    </div>

                    <div class="my-5 flex items-center gap-3 text-[#AFB5BF]">
                        <div class="h-px flex-1 bg-[#AFB5BF]"></div>
                        <span
                            class="text-[8.5px] font-semibold tracking-wider text-[#AFB5BF]"
                        >
                            {{ $t("vue.or_with_the_email") }}
                        </span>
                        <div class="h-px flex-1 bg-[#AFB5BF]"></div>
                    </div>

                    <div class="space-y-5">
                        <TextInput
                            id="email"
                            ref="input"
                            v-model="state.form.email"
                            type="email"
                            required
                            suppress-required-icon
                            autofocus
                            autocomplete="email"
                            name="email"
                            :label="$t('vue.email_address')"
                            :placeholder="
                                $t('vue.enter_your_email_address_you_example')
                            "
                            :error="getError('email')"
                            @blur="touchIfNotEmpty('email')"
                            @update:model-value="updateServerErrors('email')"
                        />

                        <TextInput
                            id="password"
                            ref="input"
                            v-model="v$.form.password.$model"
                            suppress-required-icon
                            type="password"
                            name="password"
                            required
                            autocomplete="current-password"
                            :label="$t('vue.password')"
                            :placeholder="$t('vue.enter_your_password')"
                            :error="getError('password')"
                            @blur="touchIfNotEmpty('password')"
                        />
                    </div>

                    <div class="mt-[24px]">
                        <AtomicButton
                            color="yellow"
                            size="md"
                            class="h-8 w-full px-4 font-normal font-[10.5px] font-semibold leading-4 cursor-pointer"
                            :disabled="loading"
                            type="submit"
                        >
                            {{ $t("vue.sign_in") }}
                        </AtomicButton>

                        <p
                            class="mt-[24px] text-center text-[10.5px] text-[#9FA6B2]"
                        >
                            {{ $t("vue.by_continuing_auth_agreement_text") }}
                            <a
                                class="text-[#9FA6B2] underline hover:text-custom-blue-600"
                                href="https://dmarceye.com/terms-of-service"
                                target="_blank"
                            >
                                {{ $t("vue.terms_of_service_7th_case") }}
                            </a>
                            {{ $t("vue.and") }}
                            <a
                                class="text-[#9FA6B2] underline hover:text-custom-blue-600"
                                href="https://dmarceye.com/privacy-policy"
                                target="_blank"
                            >
                                {{ $t("vue.privacy_policy_7th_case") }} </a
                            >.
                        </p>

                        <div class="mt-[24px] text-center text-[12.25px]">
                            <inertia-link
                                v-if="canResetPassword"
                                :href="withLangParam(route('password.request'))"
                                class="text-[12.25px] font-medium text-custom-blue-500 hover:text-custom-blue-600"
                            >
                                {{ $t("vue.forgot_your_password") }}
                            </inertia-link>
                        </div>

                        <p class="text-center text-[12.25px] text-gray-500">
                            {{ $t("vue.dont_have_an_account") }}
                            <inertia-link
                                :href="withLangParam(route('register'))"
                                class="text-[12.25px] font-medium text-custom-blue-500 hover:text-custom-blue-600"
                            >
                                {{ $t("vue.create_one") }}
                            </inertia-link>
                        </p>
                    </div>
                </form>
            </div>
        </div>
    </AuthLayout>
</template>

<script lang="ts" setup>
import { Head } from "@inertiajs/vue3";
import { reactive, ref } from "vue";
import route from "ziggy-js";
import { useReCaptcha } from "vue-recaptcha-v3";
import useVuelidate from "@vuelidate/core";
import { required, email } from "@vuelidate/validators";
import { router } from "@inertiajs/vue3";
import type IInputErrors from "@/types/Atomic/IInputErrors";
import Bugsnag from "@bugsnag/js";
import { useTranslations } from "@/composables/useTranslations";
import { useLangParamUrl } from "@/composables/useLangParamUrl";

defineProps({
    canResetPassword: {
        type: Boolean,
        required: true,
    },
    status: {
        type: String,
        default: () => null,
    },
});

// Get access to translations
const { t, locale } = useTranslations();
const { withLangParam } = useLangParamUrl();

const reCaptcha = useReCaptcha();
const state = reactive({
    form: {
        email: "",
        password: "",
        remember: "",
        lang: locale.value,
        recaptcha_token: "",
    },
});
const rules = {
    form: {
        email: { required, email },
        password: { required },
    },
};
const v$ = useVuelidate(rules, state);
const remember = ref(false);
const loading = ref(false);
const errors = ref<IInputErrors>({});

const touchIfNotEmpty = (field: "email" | "password") => {
    const rawValue = state.form[field];
    const value =
        typeof rawValue === "string" ? rawValue.trim() : (rawValue ?? "");

    if (value !== "") {
        // @ts-ignore
        v$.value.form[field].$touch();
    }
};

const submit = async () => {
    v$.value.form.$touch();

    if (v$.value.form.$error || loading.value) {
        return;
    }

    loading.value = true;

    try {
        if (!reCaptcha) {
            throw new Error("Recaptcha is not initialized");
        }

        await reCaptcha.recaptchaLoaded();

        const token = await reCaptcha.executeRecaptcha("login");

        if (!token) {
            throw new Error("Recaptcha token missing");
        }

        state.form.recaptcha_token = token;
    } catch (e) {
        loading.value = false;
        errors.value = { email: t("vue.recaptcha_validation_failed") };

        Bugsnag.notify(e instanceof Error ? e : new Error(String(e)));

        return;
    }

    state.form.remember = remember.value ? "on" : "";
    state.form.lang = locale.value;

    router.post(route("login"), state.form, {
        preserveScroll: true,
        onError: (err) => {
            if (typeof err === "object" && err !== null) {
                errors.value = err as IInputErrors;

                return;
            }

            Bugsnag.notify(
                err instanceof Error ? err : new Error(JSON.stringify(err)),
                (event) => {
                    event.addMetadata("inertia", { rawError: err });
                },
            );
        },
        onFinish: () => {
            loading.value = false;
            state.form.password = "";
            v$.value.$reset();
        },
    });
};

const updateServerErrors = (field: string) => {
    if (errors.value?.[field]) {
        errors.value[field] = "";
    }
};

const getError = (field: string) => {
    return (
        v$.value.form[field]?.$errors[0]?.$message ||
        errors.value?.[field] ||
        ""
    );
};
</script>
